import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiAward, 
  FiUsers, 
  FiThumbsUp, 
  FiMessageSquare, 
  FiClock, 
  FiEdit2, 
  FiSearch,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import ContestService from '../services/ContestService';

const ContestSubmissions = () => {
  const { id } = useParams();
  const history = useHistory();

  const [contest, setContest] = useState(null);
  const [posts, setPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'LEADERBOARD'
  const [page, setPage] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    fetchContestDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContestDetails = async () => {
    setLoading(true);
    try {
      const res = await ContestService.getContestById(id).catch(() => null);
      if (res) {
        setContest(res.contest || res.event || res.eventDetails || res);
        setPosts(res.posts || []);
        setLeaderboard(res.leaderboard || []);
      }
    } catch (err) {
      console.error('Error fetching contest details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getExpiryInfo = (expiryDate) => {
    if (!expiryDate) return { text: 'NO EXPIRY', isExpired: false, badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    const exp = new Date(expiryDate);
    const now = new Date();
    const diffMs = exp - now;

    if (diffMs <= 0) {
      return { text: 'EXPIRED', isExpired: true, badgeClass: 'bg-rose-50 text-rose-800 border-rose-200' };
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) {
      return { text: `LIVE (${diffDays}d left)`, isExpired: false, badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    }
    return { text: 'LIVE (Ending Soon)', isExpired: false, badgeClass: 'bg-amber-50 text-amber-800 border-amber-200' };
  };

  const displayPosts = activeTab === 'LEADERBOARD' ? leaderboard : posts;
  const filteredPosts = displayPosts.filter(p => 
    (p.userName || p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.postText || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / postsPerPage));
  const currentPosts = filteredPosts.slice((page - 1) * postsPerPage, page * postsPerPage);

  const totalVotesCast = (contest && contest.totalVotes !== undefined && contest.totalVotes !== null && contest.totalVotes > 0)
    ? contest.totalVotes
    : posts.reduce((sum, p) => sum + (Number(p.voteCount) || 0), 0);

  return (
    <div className="space-y-6 text-left pb-20">
      {/* Back Button & Actions */}
      <div className="flex items-center justify-between bg-white border border-slate-200 p-4 rounded-2xl shadow-sm">
        <button
          onClick={() => history.push('/app/contests')}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-bold transition-all flex items-center gap-2"
        >
          <FiArrowLeft className="w-4 h-4" /> Back to Contests
        </button>

        {contest && (
          <Link
            to={`/app/contests/${id}/edit`}
            className="px-4 py-2 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
          >
            <FiEdit2 className="w-3.5 h-3.5" /> Edit Contest Details
          </Link>
        )}
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-500 text-xs font-bold">Loading contest submissions & leaderboard...</div>
      ) : !contest ? (
        <div className="p-16 text-center text-slate-500 text-xs font-bold">Contest details not found.</div>
      ) : (
        <>
          {/* Contest Hero Cover & Details Card */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm relative">
            {/* Banner Cover */}
            <div className="h-52 w-full bg-slate-100 relative overflow-hidden">
              <img
                src={contest.bannerUrl || contest.banner?.url || contest.posterUrl || contest.poster?.url || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80'}
                alt={contest.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            </div>

            {/* Header Content Overlay */}
            <div className="p-6 md:p-8 -mt-16 relative z-10 space-y-6">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="flex items-end gap-4">
                  {/* Poster Thumbnail */}
                  <div className="w-24 h-24 rounded-2xl bg-white border-2 border-slate-200 overflow-hidden shadow-md shrink-0">
                    <img
                      src={contest.posterUrl || contest.poster?.url || contest.bannerUrl || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=300&q=80'}
                      alt={contest.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=300&q=80'; }}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-900 border border-purple-200 text-[10px] font-extrabold uppercase tracking-wider">
                        {contest.category || contest.categorySlug || contest.type || 'Photo Contest'}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${getExpiryInfo(contest.expiryDate).badgeClass}`}>
                        {getExpiryInfo(contest.expiryDate).text}
                      </span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight">{contest.title}</h1>
                  </div>
                </div>

                {contest.prizeonewords && (
                  <div className="px-4 py-2.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold flex items-center gap-2 self-start md:self-auto">
                    <FiAward className="w-4 h-4 text-amber-600" />
                    <span>{contest.prizeonewords}</span>
                  </div>
                )}
              </div>

              {/* KPI Quick Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-600 font-extrabold uppercase tracking-wider flex items-center gap-1">
                    <FiUsers className="text-purple-700" /> Total Submissions
                  </span>
                  <p className="text-2xl font-black text-slate-900">{posts.length || contest.participantsCount || 0}</p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-600 font-extrabold uppercase tracking-wider flex items-center gap-1">
                    <FiThumbsUp className="text-amber-600" /> Total Votes Cast
                  </span>
                  <p className="text-2xl font-black text-slate-900">{totalVotesCast || contest.totalVotes || 0}</p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-600 font-extrabold uppercase tracking-wider flex items-center gap-1">
                    <FiClock className="text-emerald-600" /> Expiry Date
                  </span>
                  <p className="text-xs font-bold text-slate-900 mt-1">
                    {contest.expiryDate ? new Date(contest.expiryDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No Expiry Set'}
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-600 font-extrabold uppercase tracking-wider flex items-center gap-1">
                    <FiAward className="text-purple-700" /> Status
                  </span>
                  <p className="text-xs font-extrabold text-emerald-800 mt-1">
                    {getExpiryInfo(contest.expiryDate).isExpired ? 'EXPIRED' : 'ACTIVE & LIVE'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submissions & Leaderboard Section Header */}
          <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setActiveTab('ALL'); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === 'ALL' ? 'bg-purple-700 text-white shadow-xs' : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                }`}
              >
                All Entries ({posts.length})
              </button>
              <button
                onClick={() => { setActiveTab('LEADERBOARD'); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === 'LEADERBOARD' ? 'bg-amber-600 text-white shadow-xs' : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                }`}
              >
                Leaderboard Ranks ({leaderboard.length})
              </button>
            </div>

            <div className="relative w-full md:w-72">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search participant or post..."
                className="w-full bg-white border border-slate-300 rounded-xl py-2 pl-9 pr-4 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-medium"
              />
              <FiSearch className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            </div>
          </div>

          {/* Submissions Grid */}
          {currentPosts.length === 0 ? (
            <div className="p-12 text-center bg-white border border-slate-200 rounded-2xl space-y-2 shadow-sm">
              <FiUsers className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-xs font-bold text-slate-700">No participant entries found matching your filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentPosts.map((post, idx) => {
                const rankNum = post.rank || (idx + 1 + (page - 1) * postsPerPage);
                const postImg = post.postImageUrl || post.postImage?.url || 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=600&q=80';
                const userPic = post.userProfilePic || post.userPic || 'https://i.pravatar.cc/150?img=12';

                return (
                  <div key={post.postId || post._id || idx} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:border-purple-300 transition-all space-y-3 p-4 flex flex-col justify-between">
                    <div className="space-y-3">
                      {/* Post Media Thumbnail */}
                      <div className="relative h-48 w-full bg-slate-100 rounded-xl overflow-hidden">
                        <img
                          src={postImg}
                          alt="Entry submission"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=600&q=80'; }}
                        />
                        <div className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-lg bg-amber-500 text-white font-black text-[10px] shadow-sm">
                          #{rankNum} Rank
                        </div>
                      </div>

                      {/* Participant User Info */}
                      <div className="flex items-center gap-3">
                        <img
                          src={userPic}
                          alt={post.userName || post.name || 'User'}
                          className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                          onError={(e) => { e.target.src = 'https://i.pravatar.cc/150?img=12'; }}
                        />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-xs font-bold text-slate-900 truncate">{post.name || post.userName || 'Anonymous Participant'}</h4>
                          <span className="text-[10px] text-slate-500 font-medium block truncate">@{post.userName || 'user'}</span>
                        </div>
                      </div>

                      {post.postText && (
                        <p className="text-xs text-slate-600 font-medium line-clamp-2 italic">"{post.postText}"</p>
                      )}
                    </div>

                    {/* Stats Footer */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600 font-medium">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 font-bold text-amber-700">
                          <FiThumbsUp className="w-3.5 h-3.5" />
                          {post.voteCount || post.totalVotes || 0} Votes
                        </span>
                        <span className="flex items-center gap-1">
                          <FiMessageSquare className="w-3.5 h-3.5 text-purple-700" />
                          {post.commentsCount || 0}
                        </span>
                      </div>

                      <span className="text-[10px] text-slate-400 font-mono">
                        {post.postedAt ? new Date(post.postedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recent'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="bg-white border border-slate-200 p-4 rounded-2xl flex items-center justify-between shadow-sm">
              <button
                disabled={page === 1}
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                className="px-4 py-2 rounded-xl bg-slate-100 disabled:opacity-50 text-slate-700 border border-slate-200 text-xs font-bold hover:bg-slate-200 transition-colors flex items-center gap-1"
              >
                <FiChevronLeft /> Previous
              </button>

              <span className="text-xs font-bold text-slate-600">
                Page <strong className="text-slate-900">{page}</strong> of <strong className="text-slate-900">{totalPages}</strong>
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                className="px-4 py-2 rounded-xl bg-slate-100 disabled:opacity-50 text-slate-700 border border-slate-200 text-xs font-bold hover:bg-slate-200 transition-colors flex items-center gap-1"
              >
                Next <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContestSubmissions;
